/**
 * Which Space should open after the one you were viewing is deleted?
 *
 * Pulled out of `SpaceRowActions` so the rule is one pure function with tests
 * rather than three lines inside a mutation callback. That callback used to do:
 *
 *   const siblings = cached.data.filter((s) => s.id !== space.id)
 *   navigate(`/app/spaces/${siblings[0].id}`)
 *
 * — which took the FIRST entry of the raw workspace-wide list. A workspace
 * spans departments, so for a Company Admin (who can see every Space in the
 * tenant) that routinely landed them in an unrelated department right after a
 * delete. It also ignored where the deleted Space actually sat, despite the
 * comment promising "the next sibling … so the sidebar selection moves
 * naturally".
 *
 * The rule now:
 *   1. never return the deleted Space;
 *   2. prefer a Space in the SAME department — org-wide Spaces (no
 *      department) form their own bucket and match each other;
 *   3. within that bucket take the NEXT Space after the deleted one, wrapping
 *      to the first, so the selection moves the way the sidebar reads;
 *   4. fall back to any other Space in the list when the department is now
 *      empty;
 *   5. return null when nothing is left, so the caller can route home.
 *
 * `spaces` MUST be the list the API returned for the current viewer — it is
 * already scoped to what they may see, so this function never widens access.
 * It deliberately does no permission logic of its own; adding a second
 * authorisation model on the client is how these bugs start.
 */
const deptKey = (space) => space?.departmentId ?? null

export function pickReplacementSpace(spaces, deletedSpace) {
  const list = Array.isArray(spaces) ? spaces : []
  const deletedId = deletedSpace?.id ?? null
  if (!deletedId) return null

  const index = list.findIndex((s) => s?.id === deletedId)
  const remaining = list.filter((s) => s?.id && s.id !== deletedId)
  if (remaining.length === 0) return null

  const sameDept = remaining.filter(
    (s) => deptKey(s) === deptKey(deletedSpace),
  )
  const pool = sameDept.length > 0 ? sameDept : remaining

  // "Next after the deleted one" is measured against the ORIGINAL positions so
  // the choice matches what the user saw in the sidebar. When the deleted
  // Space was last (or absent from the list entirely), wrap to the first.
  if (index >= 0) {
    const after = pool.find((s) => list.indexOf(s) > index)
    if (after) return after.id
  }
  return pool[0].id
}
